/**
 * The storage browser's preview pane preferences, remembered across tabs and restarts: whether the pane is open, its
 * width, and the view each content kind last used (JSON tree / formatted / raw, Markdown rendered / source, image fit).
 * Edit sessions are not here — they live with the pane (`useStoragePreviewSession`).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createGuardedJsonStorage } from '../../../../core/storage/guardedJsonStorage';

export type StoragePreviewJsonView = 'tree' | 'formatted' | 'raw';
export type StoragePreviewMarkdownView = 'rendered' | 'source';
export type StoragePreviewImageFit = 'fit' | 'actual';

export const STORAGE_PREVIEW_DEFAULT_WIDTH = 460;
export const STORAGE_PREVIEW_MIN_WIDTH = 280;
export const STORAGE_PREVIEW_MAX_WIDTH = 1400;

type PersistedPreviewPrefs = {
  open: boolean;
  widthPx: number;
  jsonView: StoragePreviewJsonView;
  markdownView: StoragePreviewMarkdownView;
  imageFit: StoragePreviewImageFit;
  /** Soft wrap in plain text views (logs, CSV, code); off by default so columns line up. */
  wrapText: boolean;
  /** The pane's height when it stacks under the grid (narrow workspaces); null = half. */
  stackedHeightPx: number | null;
};

type StoragePreviewState = PersistedPreviewPrefs & {
  setOpen: (open: boolean) => void;
  setWidthPx: (widthPx: number) => void;
  setJsonView: (view: StoragePreviewJsonView) => void;
  setMarkdownView: (view: StoragePreviewMarkdownView) => void;
  setImageFit: (fit: StoragePreviewImageFit) => void;
  setWrapText: (wrap: boolean) => void;
  setStackedHeightPx: (heightPx: number | null) => void;
};

export const clampPreviewWidth = (widthPx: number): number =>
  Math.round(Math.min(STORAGE_PREVIEW_MAX_WIDTH, Math.max(STORAGE_PREVIEW_MIN_WIDTH, Number.isFinite(widthPx) ? widthPx : STORAGE_PREVIEW_DEFAULT_WIDTH)));

export const useStoragePreviewStore = create<StoragePreviewState>()(
  persist(
    (set) => ({
      open: false,
      widthPx: STORAGE_PREVIEW_DEFAULT_WIDTH,
      jsonView: 'tree',
      markdownView: 'rendered',
      imageFit: 'fit',
      wrapText: false,
      stackedHeightPx: null,
      setOpen: (open) => set({ open }),
      setWidthPx: (widthPx) => set({ widthPx: clampPreviewWidth(widthPx) }),
      setJsonView: (jsonView) => set({ jsonView }),
      setMarkdownView: (markdownView) => set({ markdownView }),
      setImageFit: (imageFit) => set({ imageFit }),
      setWrapText: (wrapText) => set({ wrapText }),
      setStackedHeightPx: (stackedHeightPx) => set({ stackedHeightPx: stackedHeightPx === null ? null : Math.round(stackedHeightPx) }),
    }),
    {
      name: 'jam-sql-storage-preview',
      version: 1,
      storage: createGuardedJsonStorage<PersistedPreviewPrefs>(),
      partialize: ({ open, widthPx, jsonView, markdownView, imageFit, wrapText, stackedHeightPx }) => ({ open, widthPx, jsonView, markdownView, imageFit, wrapText, stackedHeightPx }),
    },
  ),
);
